import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  MssCreateRequest,
  MssUpdateRequest,
  MssListQuery,
  MssListResponse,
  MssResponse,
  MssLegendEntryResponse,
} from '@produzionejb/types';
import { api } from './api-client';

const QK = {
  list: (q: Partial<MssListQuery>) => ['msf-documents', 'list', q] as const,
  one: (id: string) => ['msf-documents', 'one', id] as const,
  legend: (country: 'ES' | 'FR') => ['msf-documents', 'legend', country] as const,
};

function toQueryString(q: Partial<MssListQuery>): string {
  const params = new URLSearchParams();
  if (q.page) params.set('page', String(q.page));
  if (q.pageSize) params.set('pageSize', String(q.pageSize));
  if (q.status) params.set('status', q.status);
  if (q.country) params.set('country', q.country);
  if (q.customerId) params.set('customerId', q.customerId);
  if (q.periodId) params.set('periodId', q.periodId);
  if (q.workDateFrom) params.set('workDateFrom', new Date(q.workDateFrom).toISOString());
  if (q.workDateTo) params.set('workDateTo', new Date(q.workDateTo).toISOString());
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useMsfList(query: Partial<MssListQuery>) {
  return useQuery({
    queryKey: QK.list(query),
    queryFn: () => api<MssListResponse>(`/api/documents/msf${toQueryString(query)}`),
    staleTime: 10_000,
  });
}

export function useMsf(id: string | undefined) {
  return useQuery({
    queryKey: id ? QK.one(id) : ['msf-documents', 'one', 'NIL'],
    queryFn: () => api<MssResponse>(`/api/documents/msf/${id}`),
    enabled: !!id,
  });
}

export function useCreateMsf() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: MssCreateRequest) =>
      api<MssResponse>('/api/documents/msf', { method: 'POST', body: data }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['msf-documents', 'list'] });
    },
  });
}

export function useUpdateMsf(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: MssUpdateRequest) =>
      api<MssResponse>(`/api/documents/msf/${id}`, { method: 'PATCH', body: data }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.one(id), updated);
      void qc.invalidateQueries({ queryKey: ['msf-documents', 'list'] });
    },
  });
}

export function useCloseMsf(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<MssResponse>(`/api/documents/msf/${id}/close`, { method: 'POST', body: {} }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.one(id), updated);
      void qc.invalidateQueries({ queryKey: ['msf-documents', 'list'] });
      void qc.invalidateQueries({ queryKey: ['msf-documents', 'legend'] });
    },
  });
}

export function useDeleteMsf() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/documents/msf/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['msf-documents', 'list'] });
    },
  });
}

export function useLegendEntries(country: 'ES' | 'FR' | null) {
  return useQuery({
    queryKey: country ? QK.legend(country) : ['msf-documents', 'legend', 'NIL'],
    queryFn: () =>
      api<{ items: MssLegendEntryResponse[]; total: number }>(`/api/documents/msf/legend/${country}`),
    enabled: !!country,
    staleTime: 30_000,
  });
}
